import { Injectable, computed, inject, signal } from '@angular/core';
import { HttpServiceService } from '../services/http-service.service';
import { AgentMessage, AgentQueryResponseData } from './agent-chat.model';

/**
 * Owns chat UI state (open/closed, message history, current conversation id)
 * and proxies API calls to HttpServiceService. The component is a thin shell
 * over this service so the same state can be inspected from anywhere (e.g.
 * the header launcher's badge).
 */
@Injectable({ providedIn: 'root' })
export class AgentChatService {
  private http = inject(HttpServiceService);

  // --- state ---
  readonly isOpen = signal<boolean>(false);
  readonly messages = signal<AgentMessage[]>([]);
  readonly conversationId = signal<string | null>(null);
  readonly isPending = computed(() => this.messages().some((m) => m.pending));

  open() {
    this.isOpen.set(true);
  }
  close() {
    this.isOpen.set(false);
  }
  toggle() {
    this.isOpen.update((v) => !v);
  }

  /** Wipe the in-memory thread — server-side conversation is left intact. */
  reset() {
    this.messages.set([]);
    this.conversationId.set(null);
  }

  async ask(question: string): Promise<void> {
    const trimmed = question.trim();
    if (!trimmed) return;

    // Append the user message and a placeholder assistant message in one go
    // so the template can render the typing indicator without flicker.
    const userMsg: AgentMessage = { role: 'user', content: trimmed };
    const pendingAssistant: AgentMessage = {
      role: 'assistant',
      content: '',
      pending: true,
    };
    this.messages.update((m) => [...m, userMsg, pendingAssistant]);

    try {
      const response = await new Promise<AgentQueryResponseData>((resolve, reject) => {
        this.http
          .agentQuery({
            question: trimmed,
            conversationId: this.conversationId() ?? undefined,
          })
          .subscribe({
            next: (resp: any) =>
              resp?.success
                ? resolve(resp.data as AgentQueryResponseData)
                : reject(new Error(resp?.message || 'Agent returned an error')),
            error: (err) => reject(err),
          });
      });

      this.conversationId.set(response.conversationId);
      this.messages.update((m) => {
        const next = [...m];
        next[next.length - 1] = {
          role: 'assistant',
          content: response.answer || '(empty response)',
          sql: response.sql,
          rows: response.rows,
          tookMs: response.tookMs,
          toolCallCount: response.toolCallCount,
        };
        return next;
      });
    } catch (err: any) {
      this.messages.update((m) => {
        const next = [...m];
        next[next.length - 1] = {
          role: 'assistant',
          content: '',
          error: err?.error?.message || err?.message || 'Request failed',
        };
        return next;
      });
    }
  }
}
